/**
 * Centralized utility for parts type map pin icons
 * These are the consistent icons used across:
 * - Marketplace map pins
 * - Wizard location picker
 * - Detail modals
 * - Listing cards
 */

import helicopterPartsIcon from '@/assets/parts-helicopter-cart.png';
import enginePartsIcon from '@/assets/parts-engine-cart.png';
import generalPartsIcon from '@/assets/parts-general-cart.png';
import rentalPartsIcon from '@/assets/parts-rental-cart.png';

export type PartsWizardType = 
  | 'parts_sell_aircraft' 
  | 'parts_sell_engine' 
  | 'parts_sell_general' 
  | 'parts_rent' 
  | 'parts_buy_aircraft' 
  | 'parts_buy_engine' 
  | 'parts_buy_general' 
  | 'parts_rent_request';

// Map parts types to their icons
export const PARTS_TYPE_MARKER_ICONS: Record<PartsWizardType, string> = {
  parts_sell_aircraft: helicopterPartsIcon,
  parts_buy_aircraft: helicopterPartsIcon,
  parts_sell_engine: enginePartsIcon,
  parts_buy_engine: enginePartsIcon,
  parts_sell_general: generalPartsIcon,
  parts_buy_general: generalPartsIcon,
  parts_rent: rentalPartsIcon,
  parts_rent_request: rentalPartsIcon,
};

/**
 * Get the marker icon for a specific parts listing type
 * @param listingType - The parts listing type
 * @returns The PNG icon path for the parts type
 */
export const getPartsTypeMarkerIcon = (listingType?: string): string => {
  if (!listingType) return generalPartsIcon;
  
  if (listingType.includes('aircraft')) return helicopterPartsIcon;
  if (listingType.includes('engine')) return enginePartsIcon;
  if (listingType === 'parts_rent' || listingType === 'parts_rent_request') return rentalPartsIcon;
  
  return generalPartsIcon;
};

/**
 * Configuration for parts type display including labels and colors
 */
export const PARTS_TYPE_CONFIG: Record<PartsWizardType, {
  label: string;
  shortLabel: string;
  isOffering: boolean;
  color: string;
  bgColor: string;
}> = {
  parts_sell_aircraft: {
    label: 'Sell Aircraft Parts',
    shortLabel: 'Sell',
    isOffering: true,
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-100',
  },
  parts_sell_engine: {
    label: 'Sell Engine Parts',
    shortLabel: 'Sell',
    isOffering: true,
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-100',
  },
  parts_sell_general: {
    label: 'Sell General Parts',
    shortLabel: 'Sell',
    isOffering: true,
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-100',
  },
  parts_rent: {
    label: 'Rent Out Parts',
    shortLabel: 'Rent',
    isOffering: true,
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-100',
  },
  parts_buy_aircraft: {
    label: 'Buy Aircraft Parts',
    shortLabel: 'Buy',
    isOffering: false,
    color: 'text-amber-600',
    bgColor: 'bg-amber-100',
  },
  parts_buy_engine: {
    label: 'Buy Engine Parts',
    shortLabel: 'Buy',
    isOffering: false,
    color: 'text-amber-600',
    bgColor: 'bg-amber-100',
  },
  parts_buy_general: {
    label: 'Buy General Parts',
    shortLabel: 'Buy',
    isOffering: false,
    color: 'text-amber-600',
    bgColor: 'bg-amber-100',
  },
  parts_rent_request: {
    label: 'Rent Parts Request',
    shortLabel: 'Rent',
    isOffering: false,
    color: 'text-amber-600',
    bgColor: 'bg-amber-100',
  },
};

/**
 * Check if a parts listing type is an offering (selling/renting out)
 */
export const isPartsOffering = (listingType?: string): boolean => {
  return listingType?.includes('sell') || listingType === 'parts_rent';
};

/**
 * Check if a parts listing type is a request (buying/seeking to rent)
 */
export const isPartsRequest = (listingType?: string): boolean => {
  return listingType?.includes('buy') || listingType === 'parts_rent_request';
};
